import {
  type CSSProperties,
  type RefObject,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { Maybe } from 'types/UtilityTypes';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

type ScrollToTopPillProps = {
  kitSectionRef: RefObject<Maybe<HTMLElement>>;
};

// Cumulative wheel-up delta (px) required before dismissing.
const WHEEL_UP_DISMISS_PX = 320;

function pillStyle(kitEl: Maybe<HTMLElement>): CSSProperties {
  const rect = kitEl?.getBoundingClientRect();
  return {
    position: 'fixed',
    top: 12,
    left: rect ? rect.left + rect.width / 2 : '50%',
    zIndex: 60,
  };
}

function isKitInViewport(kitEl: HTMLElement): boolean {
  const rect = kitEl.getBoundingClientRect();
  return rect.bottom > 80 && rect.top < window.innerHeight - 40;
}

/**
 * Kit "Scroll to top" pill.
 *
 * Shown when `showKitScrollToTop` is set (terminating action with remaining
 * suggestions). Auto-select / layout scroll will NOT dismiss it - only a real
 * user wheel-up, Kit entering the viewport, or clicking the pill.
 */
export function ScrollToTopPill({ kitSectionRef }: ScrollToTopPillProps) {
  const {
    currentState: { showKitScrollToTop },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const [style, setStyle] = useState<CSSProperties>(() =>
    pillStyle(kitSectionRef.current)
  );

  const dismiss = () => {
    setCurrentState((prev) =>
      prev.showKitScrollToTop ? { ...prev, showKitScrollToTop: false } : prev
    );
  };

  useEffect(() => {
    if (!showKitScrollToTop) {
      return;
    }
    setStyle(pillStyle(kitSectionRef.current));

    const onResize = () => setStyle(pillStyle(kitSectionRef.current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showKitScrollToTop, kitSectionRef]);

  useEffect(() => {
    if (!showKitScrollToTop) {
      return;
    }

    let wheelUpAccum = 0;

    const onWheel = (e: WheelEvent) => {
      const kitEl = kitSectionRef.current;
      if (kitEl && isKitInViewport(kitEl)) {
        dismiss();
        return;
      }

      // deltaY < 0 => user scrolling up (toward Kit)
      if (e.deltaY < 0) {
        wheelUpAccum += -e.deltaY;
        if (wheelUpAccum >= WHEEL_UP_DISMISS_PX) {
          dismiss();
        }
      } else {
        wheelUpAccum = 0;
      }
    };

    // Scrollbar / touch scroll: only dismiss when Kit actually enters the viewport.
    const onScroll = () => {
      const kitEl = kitSectionRef.current;
      if (kitEl && isKitInViewport(kitEl)) {
        dismiss();
      }
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
    };
  }, [showKitScrollToTop, kitSectionRef, setCurrentState]);

  // Chrome sidepanel scopes Tailwind to #drumkit-content-view-root
  const portalContainer = getDrumkitContainer();
  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {showKitScrollToTop && (
        <motion.button
          key='kit-scroll-to-top'
          type='button'
          onClick={() => {
            dismiss();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          style={style}
          initial={{ y: -48, opacity: 0, x: '-50%' }}
          animate={{ y: 0, opacity: 1, x: '-50%' }}
          exit={{ y: -48, opacity: 0, x: '-50%' }}
          transition={{ type: 'spring', stiffness: 420, damping: 28 }}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border-none',
            'bg-accent-main px-3.5 py-1.5 text-neutral-50 shadow-md',
            'cursor-pointer hover:opacity-90'
          )}
          aria-label='Scroll to top'
        >
          <ArrowUp
            className='h-3.5 w-3.5 shrink-0 !stroke-neutral-50'
            strokeWidth={2.5}
          />
          <Typography
            variant='body-xs'
            weight='medium'
            className='text-neutral-50 whitespace-nowrap'
          >
            Scroll to top
          </Typography>
        </motion.button>
      )}
    </AnimatePresence>,
    portalContainer
  );
}
